import { Box, Typography, Paper } from '@mui/material';
import ApexSeriesChart from '../Graphs/ApexSeriesChart';
import HeatMap from '../Graphs/HeatMap';
import { seriesDataSmall, colorsTop } from '../../data/seriesData';
import { ReactComponent as InfoIcon } from '../../assets/img/info-icon.svg';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorState } from '../common/ErrorState';
import { NoDataState } from '../common/NoDataState';

const turbineHeight = {
  bottom: '30m',
  top: '105m',
};

let subDescription: string =
  'This graph shows relative bird abundance per binned flight height from sea/ground level during the selected date range';

export const BirdHeightDistributionV2 = (props: any) => {
  const { isLoading, isError, error, hasData, data } = props;

  // Show loading state
  if (isLoading) {
    return (
      <Paper 
        className="custom-chart"
        sx={{ 
          minWidth: '1000px', 
          maxWidth: '100%', 
          minHeight: '800px', 
          marginBottom: '7px', 
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}>
        <LoadingSpinner 
          message="Loading bird height distribution data..." 
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Paper 
        className="custom-chart"
        sx={{ 
          minWidth: '1000px', 
          maxWidth: '100%', 
          minHeight: '800px', 
          marginBottom: '7px', 
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}>
        <ErrorState 
          title="Failed to load bird height distribution data"
          error={error}
          height="800px"
          showBackground={false}
          onRetry={props.onRetry}
        />
      </Paper>
    );
  }

  // Show no data state
  if (!hasData) {
    return (
      <Paper 
        className="custom-chart"
        sx={{ 
          minWidth: '1000px', 
          maxWidth: '100%', 
          minHeight: '800px', 
          marginBottom: '7px', 
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}>
        <NoDataState 
          title="No bird height distribution data available"
          message="No bird height data found for the selected date range and filters. Try adjusting your selection criteria."
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  return (
    <Paper 
      className="custom-chart"
      sx={{ 
        minWidth: '1000px', 
        maxWidth: '100%', 
        minHeight: '800px', 
        marginBottom: '7px', 
        padding: '20px',
        borderRadius: '16px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(229, 231, 235, 0.8)',
        background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        overflow: 'hidden',
      }}>
      <Box className="heatmap-header">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '23px',
            padding: '20px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '11px',
              alignItems: 'center',
              paddingTop: '7px',
            }}
          >
            <InfoIcon />
            <Box
              sx={{
                borderLeft: '2px solid #45009C',
                height: '160px',
              }}
            ></Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ color: '#17052D' }} variant="h6">
              Distribution of bird height
            </Typography>
            <Typography
              sx={{ color: '#8B8296', fontSize: '14px' }}
              variant="body2"
            >
              {subDescription}
            </Typography>
            <Box sx={{ position: 'relative', flexGrow: 1 }}>
              <Typography
                sx={{
                  color: '#8B8296',
                  fontSize: '12px',
                  position: 'absolute',
                  top: '30px',
                }}
              >
                <b>Darker</b> color indicates more birds
              </Typography>
              <Box
                sx={{
                  background: '#E8E8E8',
                  borderLeft: '1px solid #FFFFFF',
                  borderRight: '0px',
                  borderRadius: '20px 0px 0px 20px',
                  width: '5px',
                  height: '19px',
                  position: 'absolute',
                  top: '60px',
                  zIndex: 20,
                }}
              ></Box>
              <Box
                sx={{
                  marginLeft: '-23px',
                  marginTop: '22.5px',
                }}
              >
                <ApexSeriesChart
                  colors={colorsTop}
                  enableDataLabel={true}
                  strokeWidth={1}
                  graphWidth={350}
                  graphData={seriesDataSmall}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              {/* <Box
                sx={{
                  borderTop: '1px dashed #DE7F2B',
                  borderBottom: '1px dashed #DE7F2B',
                  width: '29px',
                  height: '10px',
                }}
              ></Box>
              <Typography
                sx={{
                  color: '#8B8296',
                  fontSize: '12px',
                  lineHeight: '18px',
                }}
                variant="body2"
              >
                Vicinity zone
              </Typography> */}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: 600 }}>
        <div id="heatmap" className="heat-map">
          <HeatMap data={data} turbineHeight={turbineHeight} />
        </div>
      </Box>
    </Paper>
  );
};
